import React, { useState } from 'react';
import { useLanguage } from '@/App';
import { API } from '@/App';
import axios from 'axios';
import { Send, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

// Brand Contact form KVKK metin versiyonu — DB'ye consent audit trail ile kaydedilir.
// Privacy Policy guncellendiginde bu versiyon da artirilir (v1.0 -> v1.1 vb.).
// Influencer form ile ayni versioning sistemi.
const CONSENT_TEXT_VERSION = 'v1.0';

// Submit'te validation hatasi olunca ilk hatali alana smooth scroll + focus yapar.
// KRITIK: Mobile Safari'de klavye animasyonu ~250ms; o sirada scroll calistirsak yanlis pozisyona gider.
// 300ms setTimeout + requestAnimationFrame ile browser'in bir sonraki render frame'inde,
// layout stabil olduktan sonra scroll calistirilir. Influencer form ile birebir ayni helper.
const scrollToFirstError = (fieldName) => {
  if (!fieldName) return;
  setTimeout(() => {
    requestAnimationFrame(() => {
      // 1. name attribute (input/select/textarea cogunlugu)
      let element = document.querySelector(`[name="${fieldName}"]`);
      // 2. data-testid tam eslesme
      if (!element) element = document.querySelector(`[data-testid="${fieldName}"]`);
      // 3. data-testid kismi eslesme (fallback)
      if (!element) element = document.querySelector(`[data-testid*="${fieldName}"]`);
      // 4. Fallback: sayfa ustune scroll
      if (!element) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' });
      // Smooth scroll ~500ms; focus'u ondan sonra cagir, scroll position'a etki etmesin diye preventScroll
      setTimeout(() => {
        try {
          if (typeof element.focus === 'function') {
            element.focus({ preventScroll: true });
          }
        } catch (e) {
          // intentionally ignored — bazi elementler focus desteklemez
        }
      }, 500);
    });
  }, 300);
};

const BrandContact = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  // KVKK Kurulu karari 2018/90 — Aydinlatma ve Acik Riza ayri checkbox'lar.
  // Eski tek 'privacyConsent' state'i kaldirildi; backend gecis donemi icin hala kabul ediyor.
  const [kvkkAydinlatmaConsent, setKvkkAydinlatmaConsent] = useState(false);
  const [acikRizaConsent, setAcikRizaConsent] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });

  const content = {
    tr: {
      title: 'Bizimle Çalışın',
      name: 'Adınız ve Soyadınız',
      company: 'Firma Adı',
      email: 'E-posta',
      phone: 'Telefon',
      message: 'Mesajınız',
      messagePlaceholder: 'Proje detaylarınızı ve hedeflerinizi bizimle paylaşın...',
      submit: 'Gönder',
      submitting: 'Gönderiliyor...',
      successMessage: 'İletişim formunuz alındı! En kısa sürede sizinle iletişime geçeceğiz.',
      errorMessage: 'Bir hata oluştu. Lütfen tekrar deneyin.',
      requiredField: 'Lütfen bu alanı doldurun.',
      invalidEmail: 'Lütfen geçerli bir e-posta adresi girin.',
      // KVKK consent (Kurul karari 2018/90 — Aydinlatma + Acik Riza ayri)
      aydinlatmaLinkText: 'KVKK Aydınlatma Metnini',
      aydinlatmaSuffix: ' okudum, anladım.',
      acikRizaConsentText: 'Kişisel verilerimin ve özel nitelikli kişisel verilerimin (firma bilgilerim, iletişim bilgilerim, mesaj içeriğim vb.) Persona Ajans tarafından potansiyel iş birliği değerlendirilmesi, iletişim kurulması ve teknik altyapı gereği yurt dışına aktarılmasına ilişkin Açık Rıza Beyanı\'nı okudum, onaylıyorum. ',
      acikRizaDetailLinkText: '(Detayları okuyun)',
      consentRequired: 'KVKK Aydınlatma ve Açık Rıza onaylarını işaretlemeniz gerekmektedir.'
    },
    en: {
      title: 'Work With Us',
      name: 'Your Name',
      company: 'Company Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Your Message',
      messagePlaceholder: 'Share your project details and goals with us...',
      submit: 'Send',
      submitting: 'Sending...',
      successMessage: 'Your contact form has been received! We will contact you soon.',
      errorMessage: 'An error occurred. Please try again.',
      requiredField: 'Please fill out this field.',
      invalidEmail: 'Please enter a valid email address.',
      // KVKK consent (KVKK Board decision 2018/90)
      aydinlatmaLinkText: 'KVKK Information Text',
      aydinlatmaSuffix: ' — I have read and understood.',
      acikRizaConsentText: 'I have read and I give my explicit consent to the Explicit Consent Statement regarding the processing of my personal data and special categories of personal data (company information, contact details, message content, etc.) by Persona Ajans for evaluating potential collaboration, contacting me, and transfer abroad for technical infrastructure needs. ',
      acikRizaDetailLinkText: '(Read details)',
      consentRequired: 'You must check both KVKK Information and Explicit Consent boxes.'
    }
  };

  const t = content[language];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInvalid = (e) => {
    e.target.setCustomValidity(t.requiredField);
  };

  const handleInput = (e) => {
    e.target.setCustomValidity('');
  };

  const handleEmailInvalid = (e) => {
    if (e.target.validity.valueMissing) {
      e.target.setCustomValidity(t.requiredField);
    } else if (e.target.validity.typeMismatch) {
      e.target.setCustomValidity(t.invalidEmail);
    }
  };

  const handleConsentInvalid = (e) => {
    if (!e.target.checked) {
      e.target.setCustomValidity(t.consentRequired);
    }
  };

  // Programmatic validation — { field, message } | null donduruyor.
  // 5 form alani + 2 consent kontrolu, early-return. Influencer'daki 6 ayri pipeline
  // bu basit form icin overkill olurdu; tek validate() yeterli ve okunabilir.
  // HTML5 required + setCustomValidity hala calisir (ek safety net).
  const validate = () => {
    if (!formData.name?.trim())    return { field: 'name', message: t.requiredField };
    if (!formData.company?.trim()) return { field: 'company', message: t.requiredField };
    if (!formData.email?.trim())   return { field: 'email', message: t.requiredField };
    if (!formData.phone?.trim())   return { field: 'phone', message: t.requiredField };
    if (!formData.message?.trim()) return { field: 'message', message: t.requiredField };
    if (!kvkkAydinlatmaConsent)    return { field: 'kvkk_aydinlatma_consent', message: t.consentRequired };
    if (!acikRizaConsent)          return { field: 'acik_riza_consent', message: t.consentRequired };
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Programmatic validation — hata varsa setError + scrollToFirstError + return.
    const result = validate();
    if (result) {
      setError(result.message);
      setTimeout(() => setError(''), 3000);
      scrollToFirstError(result.field);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Consent audit trail metadata — KVKK uyumlulugu icin denetim/sikayet halinde
      // hangi metin versiyonuna onay verildigini ispat etmek amaciyla DB'ye kaydedilir.
      // IP adresi backend'de alinir (frontend'den gondermek guvenilir degil).
      const consentMetadata = {
        consent_text_version: CONSENT_TEXT_VERSION,
        consent_user_agent: navigator.userAgent,
        consent_timestamp_client: new Date().toISOString(),
      };

      const payload = {
        ...formData,
        kvkk_aydinlatma_consent: kvkkAydinlatmaConsent,
        acik_riza_consent: acikRizaConsent,
        ...consentMetadata,
      };

      await axios.post(`${API}/brand-contact`, payload);
      setSuccess(true);
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        message: ''
      });
      setKvkkAydinlatmaConsent(false);
      setAcikRizaConsent(false);

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(t.errorMessage);
      console.error('Form submission error:', err);

      // Auto-hide error message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-12 bg-black bg-opacity-75 border-t border-gray-800" data-testid="contact-section">
      {/* Aria-live region — screen reader'a validation hatasi duyurulur (gorunmez, WCAG 2.1 AA) */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        id="brand-contact-error-announcer"
      >
        {error
          ? `${language === 'tr' ? 'Formda hatalar var, lütfen kontrol edin' : 'Form has errors, please review'}: ${error}`
          : ''}
      </div>

      {/* Submit Success Modal — fixed center overlay, 3sn sonra otomatik kaybolur (setTimeout handleSubmit icinde) */}
      {success && (
        <div
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-green-500/20 backdrop-blur border-2 border-green-400 text-green-300 p-6 rounded-xl shadow-2xl z-50 text-center max-w-sm"
          data-testid="success-message"
          role="status"
          aria-live="polite"
        >
          <p className="font-semibold">
            {t.successMessage}
          </p>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.title}</h2>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-900 border border-[#FFDD59] rounded-2xl shadow-xl p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.name} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInput}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.company} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInput}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-company"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.email} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onInvalid={handleEmailInvalid}
                onInput={handleInput}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-email"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.phone} <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInput}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-phone"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.message} <span className="text-red-500">*</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              onInvalid={handleInvalid}
              onInput={handleInput}
              required
              rows="6"
              placeholder={t.messagePlaceholder}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
              data-testid="input-message"
            />
          </div>

          {/* === KVKK Aydinlatma + Acik Riza (2 ayri checkbox — Kurul karari 2018/90) === */}
          {/* Brand Contact'ta toplanan veri kategorisi farkli (firma/iletisim/mesaj); */}
          {/* Acik Riza metni Brand context'ine adapte; KVKK Madde 9 yurtdisi aktarim referansi korunur. */}
          <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4 space-y-4">
            {/* Checkbox 1 — Aydinlatma */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="kvkk_aydinlatma_consent"
                checked={kvkkAydinlatmaConsent}
                onChange={(e) => setKvkkAydinlatmaConsent(e.target.checked)}
                onInvalid={handleConsentInvalid}
                onInput={handleInput}
                required
                className="mt-1 w-5 h-5 accent-yellow-400 cursor-pointer flex-shrink-0"
                data-testid="checkbox-kvkk-aydinlatma"
              />
              <span className="text-sm text-gray-200">
                <Link
                  to={language === 'tr' ? '/gizlilik-politikasi' : '/en/privacy-policy'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 underline hover:text-yellow-500"
                >
                  {t.aydinlatmaLinkText}
                </Link>
                {t.aydinlatmaSuffix} <span className="text-red-500">*</span>
              </span>
            </label>

            {/* Checkbox 2 — Acik Riza */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acik_riza_consent"
                checked={acikRizaConsent}
                onChange={(e) => setAcikRizaConsent(e.target.checked)}
                onInvalid={handleConsentInvalid}
                onInput={handleInput}
                required
                className="mt-1 w-5 h-5 accent-yellow-400 cursor-pointer flex-shrink-0"
                data-testid="checkbox-acik-riza"
              />
              <span className="text-sm text-gray-200">
                {t.acikRizaConsentText}
                <Link
                  to={language === 'tr' ? '/gizlilik-politikasi#acik-riza' : '/en/privacy-policy#explicit-consent'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 underline hover:text-yellow-500"
                >
                  {t.acikRizaDetailLinkText}
                </Link>
                <span className="text-red-500"> *</span>
              </span>
            </label>
          </div>

          {/* Submit Button or Error Message — success modal artik form section'inin disinda fixed-position */}
          <div className="relative">
            {error ? (
              <div
                className="w-full py-4 bg-red-600 text-white font-bold rounded-lg flex items-center justify-center gap-2"
                data-testid="error-message"
              >
                <span>{error}</span>
              </div>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    {t.submitting}
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    {t.submit}
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default BrandContact;
